import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { safeSaveText } from '@/lib/utils';

interface MobileItemEditorProps {
  isOpen: boolean;
  onClose: () => void;
  itemText: string;
  notes: string;
  onSave: (itemText: string, notes: string) => void;
}

export const MobileItemEditor = ({ 
  isOpen, 
  onClose, 
  itemText, 
  notes, 
  onSave 
}: MobileItemEditorProps) => {
  const [editedItemText, setEditedItemText] = useState(itemText);
  const [editedNotes, setEditedNotes] = useState(notes);

  // Sync internal state with props when they change
  useEffect(() => {
    setEditedItemText(itemText);
    setEditedNotes(notes);
  }, [itemText, notes]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEditedItemText(itemText);
      setEditedNotes(notes);
    }
  }, [isOpen, itemText, notes]);

  const handleSave = () => {
    onSave(safeSaveText(editedItemText, 500), safeSaveText(editedNotes, 1000));
    onClose();
  };

  const handleCancel = () => {
    setEditedItemText(itemText);
    setEditedNotes(notes);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] md:max-h-[90vh] min-h-[500px] md:min-h-[600px] flex flex-col p-0">
        <div className="flex flex-col h-full bg-background">
          {/* Header */}
          <div className="flex items-center justify-center p-4 border-b bg-muted/50">
            <h2 className="text-lg font-normal text-foreground">Edit Item</h2>
          </div>

          {/* Content */}
          <div className="flex-1 p-4 space-y-6">
            <div className="space-y-2">
              <label htmlFor="item-text" className="text-sm font-normal text-foreground">
                Item Description
              </label>
              <Input
                id="item-text"
                value={editedItemText}
                onChange={(e) => setEditedItemText(e.target.value)}
                placeholder="Enter item description..."
                className="text-base h-12 px-4"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="notes" className="text-sm font-normal text-foreground">
                Notes
              </label>
              <Textarea
                id="notes"
                value={editedNotes}
                onChange={(e) => setEditedNotes(e.target.value)}
                placeholder="Add any notes..."
                className="text-base min-h-[120px] px-4 py-3 resize-none"
                rows={5}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-muted/50 space-y-3">
            <Button
              onClick={handleSave}
              className="w-full h-12 text-base bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save Changes
            </Button>
            <Button
              onClick={handleCancel}
              variant="outline"
              className="w-full h-12 text-base"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};